import { apiClient } from '../api-client'
import { Milestone, PaginatedResponse } from '@/types'

type MilestoneCreateFrontendPayload = {
  title: string
  description?: string
  projectId: string
  startDate?: string
  endDate: string
  status?: string
  progress?: number
  modules?: string[]
  dependsOnMilestoneId?: string
  responsiblePersonId?: string
  isClientInputMilestone?: boolean
}

export interface MilestoneSummaryResponse {
  total: number
  completed: number
  active: number
  overdue: number
}

// Milestones API
export const milestonesApi = {
  getMilestones: async (filters?: any, page = 1, limit = 20): Promise<PaginatedResponse<Milestone> & { total_pages?: number }> => {
    const safeLimit = Math.min(Math.max(limit, 1), 100)
    const safePage = Math.max(page, 1)
    const skip = (safePage - 1) * safeLimit
    const response = await apiClient.get('/milestones', { params: { ...filters, skip, limit: safeLimit } })
    if (response && typeof response === 'object' && 'total_pages' in response && !('totalPages' in response)) {
      return { ...response, totalPages: response.total_pages }
    }
    return response
  },

  getMilestone: (id: string): Promise<Milestone> =>
    apiClient.get(`/milestones/${id}`),

  getMilestoneSummary: (filters?: {
    project_id?: string
    search?: string
    status?: string
    due_date_from?: string
    due_date_to?: string
    reporting_to_me?: boolean
    my_items?: boolean
  }): Promise<MilestoneSummaryResponse> =>
    apiClient.get('/milestones/summary', { params: filters || {} }),

  createMilestone: (data: MilestoneCreateFrontendPayload): Promise<Milestone> =>
    apiClient.post('/milestones', data),

  updateMilestone: (id: string, data: Partial<Milestone>): Promise<Milestone> =>
    apiClient.put(`/milestones/${id}`, data),

  deleteMilestone: (id: string): Promise<void> =>
    apiClient.delete(`/milestones/${id}`),

  getMilestoneTasks: (id: string): Promise<any[]> =>
    apiClient.get(`/milestones/${id}/tasks`),

  getMilestoneModules: (id: string): Promise<any> =>
    apiClient.get(`/milestones/${id}/modules`),

  getMilestoneTasksByModule: (id: string): Promise<any> =>
    apiClient.get(`/milestones/${id}/tasks-by-module`),

  addMilestoneModule: (milestoneId: string, moduleName: string): Promise<any> => {
    const formData = new FormData()
    formData.append('module_name', moduleName)
    return apiClient.post(`/milestones/${milestoneId}/modules`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  removeMilestoneModule: (milestoneId: string, moduleName: string): Promise<any> =>
    apiClient.delete(`/milestones/${milestoneId}/modules/${encodeURIComponent(moduleName)}`),

  renameMilestoneModule: (milestoneId: string, oldName: string, newName: string): Promise<any> => {
    const formData = new FormData()
    formData.append('new_module_name', newName)
    return apiClient.put(`/milestones/${milestoneId}/modules/${encodeURIComponent(oldName)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  pauseMilestoneForClient: (
    milestoneId: string,
    payload: { reason?: string; expected_client_response_at?: string }
  ): Promise<Milestone> =>
    apiClient.post(`/milestones/${milestoneId}/pause-client`, payload),

  resumeMilestoneForClient: (
    milestoneId: string,
    payload?: { resumed_at?: string; force_shift_days?: number }
  ): Promise<{
    milestone_id: string
    shift_days: number
    shifted_milestones_count: number
    shifted_tasks_count: number
    shifted_milestone_ids: string[]
    shifted_task_ids: string[]
  }> =>
    apiClient.post(`/milestones/${milestoneId}/resume-client`, payload || {}),
}
